import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthenticationGuard } from './guards/authentication.guard';
import { LoginGuard } from './guards/login.guard';
import { UsersComponent } from './components/users-manager/users/users.component';
import { UserDetailsComponent } from './components/users-manager/user-details/user-details.component';
import { DepartmentsComponent } from './components/deparment-manager/departments/departments.component';
import { LoginComponent, defaultRouteGuard } from '@card-software/library';
import { RoleGuard } from './guards/role.guard';
import { UnauthorizedComponent } from './components/unauthorized/unauthorized.component';
import { ApprovalPolicyRoles, SignatureRoles, USER_MANAGEMENT_ADMIN_ROLES } from './models/roles/roles.group';
import { UserManagerAdminGroup } from './models/authorization/enum/user-group.enum';
import { environment } from '@environments/environment';
import { ApprovalPoliciesComponent } from './components/approval-policies/approval-policies/approval-policies.component';
import { ApprovalPolicyDetailsComponent } from './components/approval-policies/approval-policy-details/approval-policy-details.component';
import { MENU_ITEMS } from './app-menu';
import { SigningDashboardComponent } from './components/signing-manager/signing-dashboard/signing-dashboard.component';
import { SessionHistoryComponent } from './components/signing-manager/session-history/session-history.component';
import { AccessCatalogComponent } from './components/access-catalog/access-catalog.component';

// E-signature pages: SignaturePreview role only, and not registered at all where the feature is off (prod), so a
// direct URL falls through to the '**' redirect.
const SIGNATURE_ROUTES: Routes = [
  {
    path: 'signature/dashboard',
    component: SigningDashboardComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: { roles: [SignatureRoles.SignaturePreview] },
  },
  {
    path: 'signature/history',
    component: SessionHistoryComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: { roles: [SignatureRoles.SignaturePreview] },
  },
];

// Access catalog (#6265), in preview: user admins only, and not registered where the feature is off (prod).
const ACCESS_CATALOG_ROUTES: Routes = [
  {
    path: 'access-catalog',
    component: AccessCatalogComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: { roles: [...USER_MANAGEMENT_ADMIN_ROLES], breadcrumb: [{ label: 'AccessCatalog_Title' }] },
  },
];

const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    canActivate: [AuthenticationGuard, defaultRouteGuard(MENU_ITEMS, 'User')],
    component: UnauthorizedComponent,
  },
  {
    path: 'users',
    component: UsersComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    // Delegated user managers use the same Users page (#6263); the API limits what they see and change.
    data: {
      roles: [...USER_MANAGEMENT_ADMIN_ROLES, UserManagerAdminGroup.UserManagerDelegate],
      breadcrumb: [{ label: 'Users_Title' }],
    },
  },
  {
    path: 'user-details',
    component: UserDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      roles: [...USER_MANAGEMENT_ADMIN_ROLES],
      breadcrumb: [{ label: 'Users_Title', route: '/users' }, { label: 'UserDetails_Title' }],
    },
  },
  {
    path: 'departments',
    component: DepartmentsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: { roles: [...USER_MANAGEMENT_ADMIN_ROLES], breadcrumb: [{ label: 'Departments_Title' }] },
  },
  {
    path: 'approval-policies',
    component: ApprovalPoliciesComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: { roles: [ApprovalPolicyRoles.ApprovalPolicyViewer, ApprovalPolicyRoles.ApprovalPolicyAdmin] },
  },
  {
    path: 'approval-policy-details',
    component: ApprovalPolicyDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: { roles: [ApprovalPolicyRoles.ApprovalPolicyViewer, ApprovalPolicyRoles.ApprovalPolicyAdmin] },
  },
  ...(environment.features.signature ? SIGNATURE_ROUTES : []),
  ...(environment.features.accessCatalog ? ACCESS_CATALOG_ROUTES : []),
  {
    path: 'login',
    component: LoginComponent,
    canActivate: [LoginGuard],
  },
  {
    path: 'unauthorized',
    component: UnauthorizedComponent,
  },
  {
    path: '**',
    redirectTo: '',
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
